import { MenuItemGroup } from '@creditornot/cb-menu';
import { DragEndEvent, DragOverEvent, DragStartEvent, UniqueIdentifier, useDndMonitor } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { arrayMove } from '@dnd-kit/sortable';
import { debounce } from 'lodash';
import { insert } from 'ramda';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Category } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { localize } from 'common/utils';
import { MenuEditorDragEventData } from 'modules/menu/components/MenuEditorDnDContext';
import { getMenuEditorDragEventData } from 'modules/menu/components/MenuEditorDnDContext/utils';
import { MenuPermissions } from 'modules/menu/types';

import CategoryLinkItem from './CategoryLinkItem';
import { DnDCategoryNavAccordionItem } from './DnDCategoryNavAccordionItem';
import { DnDCategoryNavItem } from './DnDCategoryNavItem';
import { WOLT_SPECIALS_CATEGORY_ID } from '../../constants';

type CategoryNavItemsProps = {
  language: Language;
  onUpdateCategoriesOrder: (categories: Category[]) => Promise<void>;
  onUpdateSubcategoriesOrder: (categoryId: string, subcategories: Category[]) => Promise<void>;
  onChangeSubcategoryCategory: (
    subCategoryId: string,
    parentCategoryId: string,
    subcategories: Category[],
  ) => Promise<void>;
  menuPermissions: MenuPermissions;
  hasCategoryImages: boolean;
  menuEditorRootUrl: string;
  categories: Category[];
};

type CategoryNavItemsDnDMonitorProps = {
  categories: Category[];
  onUpdateCategoriesOrder: (categories: Category[]) => Promise<void>;
  onUpdateSubcategoriesOrder: (categoryId: string, subcategories: Category[]) => Promise<void>;
  onChangeSubcategoryCategory: (
    subCategoryId: string,
    parentCategoryId: string,
    subcategories: Category[],
  ) => Promise<void>;
  children: (data: { displayCategories: Category[]; isSortingCategory: boolean }) => ReactNode;
};

const CategoryNavItemsDnDMonitor = React.memo(
  ({
    categories,
    onUpdateCategoriesOrder,
    onUpdateSubcategoriesOrder,
    onChangeSubcategoryCategory,
    children,
  }: CategoryNavItemsDnDMonitorProps) => {
    // Use cached active due to the issue with virtualized lists cause the active.data.current to get lost: https://github.com/clauderic/dnd-kit/issues/794
    const [localActiveEvent, setLocalActiveEvent] = useState<null | MenuEditorDragEventData>(null);
    const [localCategories, setLocalCategories] = useState<null | Category[]>(null);
    const displayCategories = useMemo(() => localCategories ?? categories ?? [], [categories, localCategories]);

    const reset = useCallback(() => {
      setLocalCategories(null);
      setLocalActiveEvent(null);
    }, []);

    const onDragStart = useCallback(({ active }: DragStartEvent) => {
      setLocalActiveEvent(active ? getMenuEditorDragEventData(active) : null);
    }, []);

    const onDragEnd = useCallback(
      async ({ over }: DragEndEvent) => {
        const overEvent = over && getMenuEditorDragEventData(over);
        try {
          if (!overEvent || !localActiveEvent) {
            return;
          }

          if (
            localActiveEvent.id !== overEvent.id &&
            (localActiveEvent.type === 'DND_NAVIGATION_CATEGORY' ||
              localActiveEvent.type === 'DND_ACCORDION_NAVIGATION_CATEGORY') &&
            (overEvent.type === 'DND_NAVIGATION_CATEGORY' || overEvent.type === 'DND_ACCORDION_NAVIGATION_CATEGORY')
          ) {
            const oldIndex = categories.findIndex(category => category.id === localActiveEvent.id);
            const newIndex = categories.findIndex(category => category.id === over.id);
            const updatedCategories = arrayMove(categories, oldIndex, newIndex);

            setLocalCategories(updatedCategories);

            return await onUpdateCategoriesOrder(updatedCategories);
          }

          if (
            localActiveEvent.id !== overEvent.id &&
            localActiveEvent.type === 'DND_NAVIGATION_SUBCATEGORY' &&
            overEvent.type === 'DND_NAVIGATION_SUBCATEGORY' &&
            overEvent.parentCategoryId === localActiveEvent.parentCategoryId
          ) {
            const activeSubcategories = categories.find(
              category => category.id === localActiveEvent.parentCategoryId,
            )?.child_categories;
            if (!activeSubcategories) {
              return;
            }

            const oldIndex = activeSubcategories.findIndex(category => category.id === localActiveEvent.id);
            const newIndex = activeSubcategories.findIndex(category => category.id === over.id);
            const updatedSubcategories = arrayMove(activeSubcategories, oldIndex, newIndex);
            const updatedCategories = categories.map(category => ({
              ...category,
              child_categories:
                category.id === localActiveEvent.parentCategoryId ? updatedSubcategories : category.child_categories,
            }));

            setLocalCategories(updatedCategories);

            return await onUpdateSubcategoriesOrder(localActiveEvent.parentCategoryId, updatedSubcategories);
          }

          if (
            localActiveEvent.type === 'DND_NAVIGATION_SUBCATEGORY' &&
            ['DND_NAVIGATION_SUBCATEGORY', 'DND_NAVIGATION_CATEGORY', 'DND_ACCORDION_NAVIGATION_CATEGORY'].includes(
              overEvent.type,
            )
          ) {
            const categoriesState = localCategories || categories;
            const overParentCategoryId =
              overEvent.type === 'DND_NAVIGATION_SUBCATEGORY'
                ? overEvent.parentCategoryId
                : overEvent.type === 'DND_NAVIGATION_CATEGORY'
                  ? overEvent.id
                  : overEvent.type === 'DND_ACCORDION_NAVIGATION_CATEGORY'
                    ? overEvent.category.id
                    : '';
            const overSubcategories = categoriesState.find(
              category => category.id === overParentCategoryId,
            )?.child_categories;

            if (!overSubcategories || localActiveEvent.parentCategoryId === overParentCategoryId) {
              return;
            }

            const oldIndex = overSubcategories.findIndex(category => category.id === localActiveEvent.id);
            const newIndex = ['DND_ACCORDION_NAVIGATION_CATEGORY', 'DND_NAVIGATION_CATEGORY'].includes(overEvent.type)
              ? 0
              : overSubcategories.findIndex(category => category.id === overEvent.id);
            if (oldIndex === -1 || newIndex === -1) {
              return;
            }
            const updatedSubcategories = arrayMove(overSubcategories, oldIndex, newIndex);
            const updatedCategories = categoriesState.map(category => ({
              ...category,
              child_categories:
                category.id === localActiveEvent.parentCategoryId
                  ? category.child_categories.filter(subcategory => subcategory.id !== localActiveEvent.id)
                  : category.id === overParentCategoryId
                    ? updatedSubcategories
                    : category.child_categories,
            }));

            setLocalCategories(updatedCategories);

            return await onChangeSubcategoryCategory(localActiveEvent.id, overParentCategoryId, updatedSubcategories);
          }
        } finally {
          reset();
        }
      },
      [
        localActiveEvent,
        categories,
        onUpdateCategoriesOrder,
        onUpdateSubcategoriesOrder,
        localCategories,
        onChangeSubcategoryCategory,
        reset,
      ],
    );

    const onDragOver = useCallback(
      ({ over, active }: DragOverEvent) => {
        const overEvent = over && getMenuEditorDragEventData(over);
        const activeEvent = active && getMenuEditorDragEventData(active);
        if (!overEvent || !activeEvent) {
          return;
        }

        if (
          overEvent.type === 'DND_NAVIGATION_CATEGORY' &&
          activeEvent.type === 'DND_NAVIGATION_SUBCATEGORY' &&
          overEvent.category.item_bindings.length === 0 &&
          overEvent.category.id !== activeEvent.parentCategoryId
        ) {
          const categoriesState = localCategories || categories;
          const updatedState = categoriesState.map(category => {
            if (category.id === activeEvent.parentCategoryId) {
              return {
                ...category,
                child_categories: category.child_categories.filter(subcategory => subcategory.id !== activeEvent.id),
              };
            }

            if (category.id === overEvent.category.id) {
              return {
                ...category,
                child_categories: [activeEvent.subcategory],
              };
            }

            return category;
          });

          setLocalCategories(updatedState);
        }

        if (
          ['DND_NAVIGATION_SUBCATEGORY', 'DND_ACCORDION_NAVIGATION_CATEGORY'].includes(overEvent.type) &&
          activeEvent.type === 'DND_NAVIGATION_SUBCATEGORY'
        ) {
          if (
            (overEvent.type === 'DND_NAVIGATION_SUBCATEGORY' &&
              overEvent.parentCategoryId === activeEvent.parentCategoryId) ||
            (overEvent.type === 'DND_ACCORDION_NAVIGATION_CATEGORY' &&
              overEvent.category.id === activeEvent.parentCategoryId)
          ) {
            return;
          }

          const categoriesState = localCategories || categories;
          const updatedState = categoriesState.map(category => {
            if (category.id === activeEvent.parentCategoryId) {
              return {
                ...category,
                child_categories: category.child_categories.filter(subcategory => subcategory.id !== activeEvent.id),
              };
            }

            if (overEvent.type === 'DND_NAVIGATION_SUBCATEGORY' && category.id === overEvent.parentCategoryId) {
              const subcategoryIndex = category.child_categories.findIndex(
                subcategory => subcategory.id === overEvent.id,
              );
              return {
                ...category,
                child_categories: insert(subcategoryIndex, activeEvent.subcategory, category.child_categories),
              };
            }

            if (overEvent.type === 'DND_ACCORDION_NAVIGATION_CATEGORY' && category.id === overEvent.category.id) {
              return {
                ...category,
                child_categories: insert(0, activeEvent.subcategory, category.child_categories),
              };
            }

            return category;
          });

          setLocalCategories(updatedState);
        }
      },
      [categories, localCategories],
    );

    useDndMonitor({
      onDragStart,
      onDragEnd,
      onDragCancel: reset,
      onDragOver: debounce(onDragOver, 100),
    });

    return (
      <>
        {children({
          displayCategories,
          isSortingCategory: !!(localActiveEvent?.type === 'DND_NAVIGATION_CATEGORY'),
        })}
      </>
    );
  },
);

const CategoryNavItems = ({
  language,
  onUpdateCategoriesOrder,
  onUpdateSubcategoriesOrder,
  onChangeSubcategoryCategory,
  menuPermissions,
  hasCategoryImages,
  menuEditorRootUrl,
  categories,
}: CategoryNavItemsProps) => {
  const specialCategory = categories.find(category => category.id === WOLT_SPECIALS_CATEGORY_ID);

  return (
    <MenuItemGroup title={<FormattedMessage id="menu.categories" />}>
      {menuPermissions.woltSpecials && specialCategory && (
        <CategoryLinkItem
          numberOfItems={specialCategory.item_bindings.length}
          imageUrl={hasCategoryImages && specialCategory.image ? specialCategory.image : null}
          imageBlurhash={specialCategory.image_blurhash ?? null}
          href={`${menuEditorRootUrl}/${specialCategory.id}`}
        >
          {localize(specialCategory.name, language)}
        </CategoryLinkItem>
      )}
      <span>
        <CategoryNavItemsDnDMonitor
          categories={categories}
          onUpdateCategoriesOrder={onUpdateCategoriesOrder}
          onUpdateSubcategoriesOrder={onUpdateSubcategoriesOrder}
          onChangeSubcategoryCategory={onChangeSubcategoryCategory}
        >
          {({ displayCategories, isSortingCategory }) => {
            const filteredDisplayCategories = displayCategories.filter(
              category => category.id !== WOLT_SPECIALS_CATEGORY_ID,
            );
            const itemUniqueIdentifiers: UniqueIdentifier[] = filteredDisplayCategories.map(category => category.id);
            return (
              <SortableContext items={itemUniqueIdentifiers} strategy={verticalListSortingStrategy}>
                {filteredDisplayCategories.map(category =>
                  !!category.child_categories.length ? (
                    <DnDCategoryNavAccordionItem
                      disabledSubcategorySort={isSortingCategory}
                      key={category.id}
                      menuEditorRootUrl={menuEditorRootUrl}
                      language={language}
                      category={category}
                      hasCategoryImages={hasCategoryImages}
                    />
                  ) : (
                    <DnDCategoryNavItem
                      key={category.id}
                      menuEditorRootUrl={menuEditorRootUrl}
                      language={language}
                      category={category}
                      hasCategoryImages={hasCategoryImages}
                    />
                  ),
                )}
              </SortableContext>
            );
          }}
        </CategoryNavItemsDnDMonitor>
      </span>
    </MenuItemGroup>
  );
};

export default React.memo(CategoryNavItems);
